import React from 'react';
import { motion } from 'motion/react';
import { Pizza, Scissors, ShoppingBag, Briefcase } from 'lucide-react';
import {
  fadeInUp,
  staggerContainer,
  staggerItem,
  messageReveal,
  hoverLift,
} from '../../lib/motion';
import { openWhatsApp } from '../../lib/contact';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';

interface SampleBubble {
  from: 'user' | 'bot';
  text: string;
}

interface SectorUseCase {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  outcome: string;
  message: string;
  sample: SampleBubble[];
}

const SECTORS: SectorUseCase[] = [
  {
    icon: Pizza,
    title: 'Pizzeria',
    outcome: 'Ordini e asporto gestiti in automatico, anche fuori orario',
    message: 'Ciao, ho una pizzeria e vorrei automatizzare ordini e asporto su WhatsApp con ChatLy',
    sample: [
      { from: 'user', text: 'Due margherite per le 20:30?' },
      { from: 'bot', text: 'Perfetto, ordine confermato per le 20:30' },
    ],
  },
  {
    icon: Scissors,
    title: 'Parrucchiere',
    outcome: 'Prenotazioni e promemoria che azzerano i no-show',
    message: 'Ciao, ho un salone da parrucchiere e vorrei gestire prenotazioni e promemoria con ChatLy',
    sample: [
      { from: 'user', text: 'Posso prenotare un taglio venerdì?' },
      { from: 'bot', text: 'Venerdì alle 16:00 è libero, ti prenoto?' },
    ],
  },
  {
    icon: ShoppingBag,
    title: 'E-commerce',
    outcome: 'Catalogo, ordini e tracking direttamente in chat',
    message: 'Ciao, ho un e-commerce e vorrei gestire catalogo, ordini e tracking in chat con ChatLy',
    sample: [
      { from: 'user', text: "Dov'è il mio ordine #2048?" },
      { from: 'bot', text: 'In consegna domani, ti invio il link di tracking' },
    ],
  },
  {
    icon: Briefcase,
    title: 'Studio professionale',
    outcome: 'Appuntamenti e prima qualifica delle richieste',
    message: 'Ciao, ho uno studio professionale e vorrei gestire appuntamenti e qualifica richieste con ChatLy',
    sample: [
      { from: 'user', text: 'Avrei bisogno di una consulenza' },
      { from: 'bot', text: 'Certo, di cosa hai bisogno nello specifico?' },
    ],
  },
];

const UseCasesBySector: React.FC = () => {
  return (
    <section id="settori" className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        <motion.div {...fadeInUp} className="text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-heading font-bold text-text-primary mb-6">
            Casi d'uso per <span className="text-primary">settore</span>
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            ChatLy si adatta al tuo business. Scopri come automatizza le conversazioni nel tuo settore.
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto"
        >
          {SECTORS.map((sector) => (
            <motion.div
              key={sector.title}
              variants={staggerItem}
              whileHover={{ y: -6 }}
              transition={hoverLift}
              className="h-full bg-card border border-border rounded-2xl shadow-sm hover:shadow-md hover:border-primary/30 transition-shadow duration-300 p-8 flex flex-col"
            >
              <div className="flex items-center gap-4 mb-5">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <sector.icon className="h-7 w-7 text-primary" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-heading font-bold text-text-primary">
                  {sector.title}
                </h3>
              </div>

              <p className="text-text-secondary leading-relaxed mb-6">
                {sector.outcome}
              </p>

              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="space-y-2 mb-7 rounded-2xl bg-muted/40 p-4"
              >
                {sector.sample.map((bubble, i) => (
                  <motion.div
                    key={i}
                    variants={messageReveal}
                    className={cn(
                      'flex',
                      bubble.from === 'user' ? 'justify-end' : 'justify-start'
                    )}
                  >
                    <span
                      className={cn(
                        'inline-block max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-snug',
                        bubble.from === 'user'
                          ? 'bg-primary text-white rounded-br-sm'
                          : 'bg-card text-text-primary border border-border rounded-bl-sm'
                      )}
                    >
                      {bubble.text}
                    </span>
                  </motion.div>
                ))}
              </motion.div>

              <div className="mt-auto">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => openWhatsApp(sector.message)}
                >
                  Attiva per {sector.title.toLowerCase()}
                </Button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default UseCasesBySector;
